/**
 * Service Offerings Store Slice
 *
 * Zustand slice for service offering state management.
 * Domain: ServicesContext > ServiceOfferingAggregate
 *
 * Manages marketplace browse state and org service management.
 * Integrates with serviceOfferingsApi for listing and detail retrieval.
 */

import {
  clearError as clearOpError,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { serviceOfferingsApi } from '@/api/serviceOfferings';
import type { ServiceOffering } from '@/types/service-offering.types';

import type { ErrorStates, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface ServiceOfferingsSlice {
  // Marketplace state
  marketplaceOfferings: ServiceOffering[];
  marketplaceNextCursor: string | null;
  selectedOffering: ServiceOffering | null;
  // Org management state
  orgOfferings: ServiceOffering[];
  orgOfferingsNextCursor: string | null;
  // Loading/error
  serviceOfferingsIsLoading: LoadingStates;
  serviceOfferingsHasLoaded: LoadingStates;
  serviceOfferingsLastLoaded: LoadedTimestamps;
  serviceOfferingsErrors: ErrorStates;
  // Actions
  fetchMarketplaceOfferings: (params?: {
    cursor?: string;
    category?: string;
    limit?: number;
  }) => Promise<void>;
  fetchOfferingBySlug: (slug: string) => Promise<void>;
  fetchOrgOfferings: (orgId: string, params?: { cursor?: string; limit?: number }) => Promise<void>;
  setSelectedOffering: (offering: ServiceOffering | null) => void;
  clearServiceOfferingsError: (operation?: string) => void;
  resetServiceOfferings: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialServiceOfferingsState = {
  marketplaceOfferings: [],
  marketplaceNextCursor: null,
  selectedOffering: null,
  orgOfferings: [],
  orgOfferingsNextCursor: null,
  serviceOfferingsIsLoading: {},
  serviceOfferingsHasLoaded: {},
  serviceOfferingsLastLoaded: {},
  serviceOfferingsErrors: {},
};

// ============================================================================
// Slice Creator
// ============================================================================

export const createServiceOfferingsSlice: StateCreator<
  ServiceOfferingsSlice,
  [],
  [],
  ServiceOfferingsSlice
> = (set, get) => ({
  ...initialServiceOfferingsState,

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Fetch service offerings for the public marketplace
   */
  fetchMarketplaceOfferings: async (params?: {
    cursor?: string;
    category?: string;
    limit?: number;
  }) => {
    const operation = 'fetchMarketplaceOfferings';
    const currentState = get();

    if (isOperationLoading(currentState.serviceOfferingsIsLoading, operation)) return;

    set({
      serviceOfferingsIsLoading: setLoading(
        currentState.serviceOfferingsIsLoading,
        operation,
        true
      ),
      serviceOfferingsErrors: clearOpError(currentState.serviceOfferingsErrors, operation),
    });

    try {
      const response = await serviceOfferingsApi.listMarketplace(params);

      const state = get();
      const loadedState = setLoaded(
        state.serviceOfferingsHasLoaded,
        state.serviceOfferingsLastLoaded,
        operation,
        true
      );

      set({
        marketplaceOfferings: response.offerings,
        marketplaceNextCursor: response.nextCursor ?? null,
        serviceOfferingsIsLoading: setLoading(state.serviceOfferingsIsLoading, operation, false),
        serviceOfferingsHasLoaded: loadedState.hasLoaded,
        serviceOfferingsLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(
        state.serviceOfferingsHasLoaded,
        state.serviceOfferingsLastLoaded,
        operation,
        false
      );

      set({
        serviceOfferingsIsLoading: setLoading(state.serviceOfferingsIsLoading, operation, false),
        serviceOfferingsErrors: setError(
          state.serviceOfferingsErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch marketplace offerings'
        ),
        serviceOfferingsHasLoaded: loadedState.hasLoaded,
        serviceOfferingsLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * Fetch a single service offering by slug
   */
  fetchOfferingBySlug: async (slug: string) => {
    const operation = 'fetchOfferingBySlug';
    const currentState = get();

    if (isOperationLoading(currentState.serviceOfferingsIsLoading, operation)) return;

    set({
      serviceOfferingsIsLoading: setLoading(
        currentState.serviceOfferingsIsLoading,
        operation,
        true
      ),
      serviceOfferingsErrors: clearOpError(currentState.serviceOfferingsErrors, operation),
    });

    try {
      const offering = await serviceOfferingsApi.getBySlug(slug);

      const state = get();
      const loadedState = setLoaded(
        state.serviceOfferingsHasLoaded,
        state.serviceOfferingsLastLoaded,
        operation,
        true
      );

      set({
        selectedOffering: offering,
        serviceOfferingsIsLoading: setLoading(state.serviceOfferingsIsLoading, operation, false),
        serviceOfferingsHasLoaded: loadedState.hasLoaded,
        serviceOfferingsLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(
        state.serviceOfferingsHasLoaded,
        state.serviceOfferingsLastLoaded,
        operation,
        false
      );

      set({
        serviceOfferingsIsLoading: setLoading(state.serviceOfferingsIsLoading, operation, false),
        serviceOfferingsErrors: setError(
          state.serviceOfferingsErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch service offering'
        ),
        serviceOfferingsHasLoaded: loadedState.hasLoaded,
        serviceOfferingsLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * Fetch service offerings for an organization
   */
  fetchOrgOfferings: async (orgId: string, params?: { cursor?: string; limit?: number }) => {
    const operation = 'fetchOrgOfferings';
    const currentState = get();

    if (isOperationLoading(currentState.serviceOfferingsIsLoading, operation)) return;

    set({
      serviceOfferingsIsLoading: setLoading(
        currentState.serviceOfferingsIsLoading,
        operation,
        true
      ),
      serviceOfferingsErrors: clearOpError(currentState.serviceOfferingsErrors, operation),
    });

    try {
      const response = await serviceOfferingsApi.listOrgOfferings(orgId, params);

      const state = get();
      const loadedState = setLoaded(
        state.serviceOfferingsHasLoaded,
        state.serviceOfferingsLastLoaded,
        operation,
        true
      );

      set({
        orgOfferings: response.offerings,
        orgOfferingsNextCursor: response.nextCursor ?? null,
        serviceOfferingsIsLoading: setLoading(state.serviceOfferingsIsLoading, operation, false),
        serviceOfferingsHasLoaded: loadedState.hasLoaded,
        serviceOfferingsLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(
        state.serviceOfferingsHasLoaded,
        state.serviceOfferingsLastLoaded,
        operation,
        false
      );

      set({
        serviceOfferingsIsLoading: setLoading(state.serviceOfferingsIsLoading, operation, false),
        serviceOfferingsErrors: setError(
          state.serviceOfferingsErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch org service offerings'
        ),
        serviceOfferingsHasLoaded: loadedState.hasLoaded,
        serviceOfferingsLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * Set the currently selected offering (for detail view)
   */
  setSelectedOffering: (offering: ServiceOffering | null) => {
    set({ selectedOffering: offering });
  },

  /**
   * Clear error for a specific operation or all errors
   */
  clearServiceOfferingsError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({
        serviceOfferingsErrors: clearOpError(currentState.serviceOfferingsErrors, operation),
      });
    } else {
      set({ serviceOfferingsErrors: {} });
    }
  },

  /**
   * Reset service offerings state to initial values
   */
  resetServiceOfferings: () => {
    set(initialServiceOfferingsState);
  },
});
